import React from "react";
import { Bookmark, Check, Share2 } from "lucide-react";
import { parseISO, format, formatDistanceToNowStrict } from "date-fns";
import FriendlyFlag from "./FriendlyFlag";
import {
  TeamCategoryPill,
  LocationModePill,
  OpponentTierLine,
  FlexibleBadge,
  CampBadge,
  MultipleMatchesBadge,
} from "./FriendlyPills";

// The TP-Friendlies browse card. Structure adopts the Last-Minute ListingCard
// 3-slot shape (crest header · meta · footer); content is friendlies-native. The
// caller supplies the apply/saved handlers so the card stays surface-agnostic.
//
// No-geo fallback (DECISIONS D-005): the platform has no club coordinates, so the
// distance line ALWAYS degrades to country/region text (grey dot) — never a "~km"
// figure. `max_distance_km` is shown only as the poster's own travel willingness.

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const toDate = (v) => {
  if (!v) return null;
  try {
    const d = parseISO(v);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (e) {
    return null;
  }
};

const timeAgo = (iso) => {
  const d = toDate(iso);
  if (!d) return "";
  try {
    return `${formatDistanceToNowStrict(d)} ago`;
  } catch (e) {
    return "";
  }
};

// Left date rail — fixed date, flexible range, or a camp window.
const DateRail = ({ request }) => {
  const isCamp = request.location?.mode === "camp";
  const isFlexible = request.date?.is_flexible;
  const start = toDate(request.start_date || request.date?.date_start);

  if (isCamp || isFlexible) {
    return (
      <div className="w-[86px] flex-shrink-0 pr-3 border-r border-[#EEF1F5] flex flex-col items-start gap-1.5">
        {isCamp ? <CampBadge /> : <FlexibleBadge />}
        <span className="text-[13px] font-bold text-[#0F1728] leading-tight">{request.date?.phrase}</span>
        {isCamp && start && <span className="text-[11px] text-[#98A2B3]">{format(start, "yyyy")}</span>}
      </div>
    );
  }
  // Fixed date: DOW / big day / MON (+ optional kickoff).
  return (
    <div className="w-[86px] flex-shrink-0 pr-3 border-r border-[#EEF1F5] flex flex-col items-start">
      {start ? (
        <>
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#98A2B3]">{format(start, "EEE")}</span>
          <span className="text-[27px] font-extrabold text-[#0F1728] leading-none">{format(start, "d")}</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#66707F]">{format(start, "MMM")}</span>
        </>
      ) : (
        <span className="text-[13px] font-bold text-[#0F1728]">{request.date?.phrase}</span>
      )}
      {request.date?.kickoff_time && (
        <span className="mt-1 text-[11px] font-medium text-[#2283FF]">{request.date.kickoff_time}</span>
      )}
    </div>
  );
};

// Distance line — always the no-geo fallback (D-005): destination for camps, home
// country otherwise. Never a "~km from you" figure.
const GeoFallbackLine = ({ request }) => {
  const loc = request.location || {};
  const parts =
    loc.mode === "camp"
      ? [loc.region, loc.country?.name].filter(Boolean)
      : [loc.country?.name].filter(Boolean);
  if (!parts.length) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-[#66707F]">
      <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ backgroundColor: "#C3CCD9" }} />
      {parts.join(" · ")}
    </span>
  );
};

const AppliedState = () => (
  <span
    className="inline-flex items-center justify-center gap-1.5 w-full min-h-[44px] px-3 rounded-lg text-[12.5px] font-semibold"
    style={{ backgroundColor: "#E7F6EF", color: "#0E7B52" }}
    data-testid="friendly-applied"
  >
    <Check className="w-4 h-4" /> Applied
  </span>
);

const FriendlyCard = ({ request, onApply, onToggleSave, onOpenChat, onShare }) => {
  const applied = request.my_application?.state === "applied";
  const inChat = request.my_application?.state === "in_chat";
  // A camp match agreed from chat sets state=in_chat WITHOUT a channel_cid, so the
  // "Open chat" CTA degrades to a quiet "In chat" label when there's no channel to open.
  const inChatCid = request.my_application?.channel_cid;
  const saved = !!request.saved;
  const club = request.club || {};
  const isCamp = request.location?.mode === "camp";
  const showTravel = request.location?.mode === "travel" && request.max_distance_km;

  return (
    <div
      className="bg-white rounded-[12px] p-4 border border-[#E6EAF0] flex flex-wrap sm:flex-nowrap gap-3"
      style={{ boxShadow: "0 1px 2px rgba(16,24,40,.04)" }}
      data-testid="friendly-card"
    >
      <DateRail request={request} />

      {/* Middle */}
      <div className="flex-1 min-w-[55%] sm:min-w-0">
        <div className="flex items-start gap-2.5">
          {club.crest ? (
            <img src={club.crest} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" referrerPolicy="no-referrer" />
          ) : (
            <span className="w-9 h-9 rounded-full bg-[#0B3E8F] text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0">
              {initials(club.name)}
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className="text-[14.5px] font-bold text-[#0F1728] truncate">{club.name}</h3>
              <FriendlyFlag country={request.country} />
              {request.country?.name && <span className="text-[12px] text-[#98A2B3] truncate">{request.country.name}</span>}
            </div>
            <div className="flex items-center flex-wrap gap-1.5 mt-1">
              <TeamCategoryPill label={request.team_category_label} />
              {request.team_label && <span className="text-[11.5px] text-[#66707F] font-medium">{request.team_label}</span>}
            </div>
          </div>
        </div>

        {/* Meta row: location mode · tier · (multiple matches) */}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 mt-2.5">
          <LocationModePill location={request.location} />
          <OpponentTierLine display={request.tier?.display} />
          {isCamp && request.camp_multiple_matches && <MultipleMatchesBadge />}
        </div>

        {/* Geo fallback + travel willingness */}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
          <GeoFallbackLine request={request} />
          {showTravel && (
            <span className="text-[11.5px] text-[#98A2B3]">Can travel up to {request.max_distance_km} km</span>
          )}
        </div>

        {request.context_line && (
          <p className="mt-2 text-[12.5px] text-[#66707F] leading-snug">{request.context_line}</p>
        )}
      </div>

      {/* Right rail: bookmark + share · CTA · posted. On mobile it wraps to a
          full-width row (icons left, CTA filling the rest) for one-thumb use. */}
      <div className="flex flex-row-reverse sm:flex-col items-center sm:items-end justify-between w-full sm:w-[112px] flex-shrink-0 gap-2 sm:gap-0 order-last sm:order-none">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onToggleSave && onToggleSave(request)}
            className="min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-1.5 rounded-lg hover:bg-gray-50 text-[#98A2B3] flex items-center justify-center"
            aria-label={saved ? "Remove bookmark" : "Save for later"}
            aria-pressed={saved}
            title={saved ? "Saved" : "Save for later"}
            data-testid="friendly-bookmark"
          >
            <Bookmark className="w-[18px] h-[18px]" style={saved ? { fill: "#2283FF", color: "#2283FF" } : undefined} />
          </button>
          {onShare && (
            <button
              type="button"
              onClick={() => onShare(request)}
              className="min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 p-1.5 rounded-lg hover:bg-gray-50 text-[#98A2B3] flex items-center justify-center"
              aria-label="Share request"
              title="Share"
              data-testid="friendly-share"
            >
              <Share2 className="w-[16px] h-[16px]" />
            </button>
          )}
        </div>

        <div className="flex-1 sm:flex-none w-auto sm:w-full sm:mt-2">
          {applied ? (
            <AppliedState />
          ) : inChat ? (
            inChatCid ? (
              <button
                type="button"
                onClick={() => onOpenChat && onOpenChat(request)}
                className="w-full min-h-[44px] px-3 rounded-lg bg-white border border-[#2283FF] text-[#1866CC] text-[12.5px] font-semibold hover:bg-[#F0F7FF]"
              >
                Open chat
              </button>
            ) : (
              <span className="inline-flex items-center justify-center w-full min-h-[44px] px-3 rounded-lg bg-[#EEF1F6] text-[#475467] text-[12.5px] font-semibold">
                In chat
              </span>
            )
          ) : (
            <button
              type="button"
              onClick={() => onApply && onApply(request)}
              className="w-full min-h-[44px] px-3 rounded-lg bg-[#2283FF] text-white text-[12.5px] font-semibold hover:bg-[#186FE0]"
              data-testid="friendly-apply-cta"
            >
              Apply
            </button>
          )}
          {request.created_at && (
            <p className="mt-1.5 text-right text-[11px] text-[#98A2B3]">Posted {timeAgo(request.created_at)}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendlyCard;
